import { useEffect, useState, useCallback, useRef } from "react";
import { createApiService } from "../services/api";
import { useAuth } from "./useAuth";

export interface UseResourceOptions<T> {
    resourceName: string;
    id?: string; // For single resource operations
    skipFetch?: boolean; // Skip initial fetch
    onSuccess?: (data: T | T[]) => void;
    onError?: (error: Error) => void;
    // New options for nested resources
    parentResource?: string;
    parentId?: string;
    // Allow fetching without authentication (for public resources)
    allowUnauthenticated?: boolean;
}

export interface UseResourceReturn<T> {
    data: T | T[] | null;
    loading: boolean;
    error: string | null;
    create: (data: T) => Promise<void>;
    update: (id: string, data: T) => Promise<void>;
    remove: (id: string) => Promise<void>;
    refresh: () => Promise<void>;
}

export const useResource = <T>(options: UseResourceOptions<T>): UseResourceReturn<T> => {
    const { resourceName, id, skipFetch = false, onSuccess, onError, parentResource, parentId, allowUnauthenticated = false } = options;
    const { token, isAuthenticated, loading: authLoading } = useAuth();
    
    const [data, setData] = useState<T | T[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fetchingRef = useRef(false);

    const fetchData = useCallback(async () => {
        // Prevent concurrent fetches
        if (fetchingRef.current) return;
        
        // Wait for auth to finish loading before deciding which endpoint to use
        if (authLoading) return;
        
        fetchingRef.current = true;
        
        try {
            setLoading(true);
            setError(null);
            // For unauthenticated requests, use the public API endpoint
            // Only use public endpoint if explicitly not authenticated (not just loading)
            if (allowUnauthenticated && !isAuthenticated && !authLoading) {
                // Create a temporary API service instance for public access (no token needed)
                const publicApiService = createApiService<T>('', resourceName, parentResource, parentId);
                
                if (id) {
                    // Fetch single resource by ID
                    const result = await publicApiService.getByIdPublic(id);
                    setData(result);
                    onSuccess?.(result as T);
                } else if (parentResource && parentId) {
                    // Fetch nested resources (e.g., coaches in a league)
                    const result = await publicApiService.getAllPublic();
                    setData(result);
                    onSuccess?.(result as T[]);
                } else {
                    // No ID and no parent - can't fetch from public endpoint
                    setError('Cannot fetch resource: public endpoint requires either an ID or parent resource');
                }
                return;
            }
            
            // For authenticated users, always use the authenticated endpoint
            // Double-check token is a valid non-empty string before creating API service
            if (isAuthenticated && token && typeof token === 'string' && token.length > 0) {
                const apiService = createApiService<T>(token, resourceName, parentResource, parentId);

                if (id) {
                    // Fetch single resource (always require auth for authenticated endpoint)
                    const result = await apiService.getById(id, true);
                    setData(result);
                    onSuccess?.(result as T);
                } else {
                    // Fetch all resources
                    const result = await apiService.getAll();
                    setData(result);
                    onSuccess?.(result as T[]);
                }
                return;
            }
            
            // If not authenticated and not allowing unauthenticated, don't fetch
            if (!isAuthenticated && !allowUnauthenticated) return;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An error occurred';
            setError(errorMessage);
            onError?.(err as Error);
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    }, [authLoading, allowUnauthenticated, isAuthenticated, id, resourceName, parentResource, parentId, token]);

    const create = async (newData: T) => {
        if (!token || !isAuthenticated) return;

        try {
            setLoading(true);
            setError(null);

            const apiService = createApiService<T>(token, resourceName, parentResource, parentId);

            // Extract the ID from the data object for top-level resources
            // For nested resources, use the parent ID
            const resourceId = parentResource && parentId 
                ? parentId 
                : (newData as any).id || (newData as any).leagueId || (newData as any).seasonId || (newData as any).coachId || '';
            
            await apiService.create(resourceId, newData);
            
            // Refresh the data
            await fetchData();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to create resource';
            setError(errorMessage);
            onError?.(err as Error);
        } finally {
            setLoading(false);
        }
    };

    const update = async (resourceId: string, updatedData: T) => {
        if (!token || !isAuthenticated) return;

        try {
            setLoading(true);
            setError(null);

            const apiService = createApiService<T>(token, resourceName, parentResource, parentId);
            await apiService.update(resourceId, updatedData);
            
            // Refresh the data
            await fetchData();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to update resource';
            setError(errorMessage);
            onError?.(err as Error);
        } finally {
            setLoading(false);
        }
    };

    const remove = async (resourceId: string) => {
        if (!token || !isAuthenticated) return;

        try {
            setLoading(true);
            setError(null);

            const apiService = createApiService<T>(token, resourceName, parentResource, parentId);
            await apiService.delete(resourceId);
            
            // Refresh the data
            await fetchData();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete resource';
            setError(errorMessage);
            onError?.(err as Error);
        } finally {
            setLoading(false);
        }
    };

    const refresh = async () => {
        await fetchData();
    };

    // Fetch data when component mounts or dependencies change
    useEffect(() => {
        // Wait for auth to finish loading before fetching
        if (authLoading) return;
        
        // For authenticated requests, ensure we have a token before fetching
        if (isAuthenticated && !token) return;
        
        if (!skipFetch && (isAuthenticated || allowUnauthenticated)) {
            fetchData();
        }
        // fetchData is stable enough - it only changes when actual dependencies (id, resourceName, etc.) change
        // We include token here to ensure we re-fetch when auth token becomes available
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, authLoading, id, skipFetch, parentId, allowUnauthenticated, token]);

    return {
        data,
        loading,
        error,
        create,
        update,
        remove,
        refresh,
    };
}; 